<template>
	<div class="friendsList leaderboard-block">
		<div v-if="loading" class="spinner" style="position: inherit; line-height: inherit; height: auto;">
			<Loader />
		</div>

		<template v-else>
			<div v-if="!connectionStatus.facebook" @click="facebookLogin()">
				<img :src="'/customer/images/leaderboard-banner.png'" style="cursor: pointer; max-width: 100%;">

				<div class="mt-3" v-html="t.mainLeaderboardIntroText" />
				
				<button class="btn-login-facebook mt-4" @click.stop="facebookLogin()">
					<FontAwesomeIcon :icon="['fab', 'facebook-f']" />
					{{ t.mainLeaderboardFacebookConnectButton }}
				</button>
			</div>

			<div v-if="connectionStatus.facebook" class="mb-3">
				<div v-if="showTitle" class="default-block-title mb-3">
					{{ t.leaderboardTitle }}
				</div>

				<div v-for="(friend, index) in friends" :key="friend.id" :class="{'me': friend.me === true}" class="friend d-flex flex-vertical-align">
					<div class="rank">
						{{ (index as number) + 1 }}.
					</div>
					<div class="image user-thumbnail">
						<UserThumbnail :image-url="friend.me ? userImage : friend.picture_url" />
					</div>
					<div class="info-block">
						<div class="name">
							{{ friend.me ? userFullName : friend.name }}
						</div>
						<div class="points">
							{{ friend.me ? points : friend.points }}
						</div>
					</div>
				</div>
				
				<div v-if="!friendsExists && connectionStatus.facebook != 'disconnected'">
					<!-- It's only me here -->
					<div class="block-no-items">
						{{ t.mainLeaderboardOnlyOneUserMessage }}
					</div>
				</div>
			</div>

			<div v-if="connectionStatus.facebook == 'disconnected' || connectionStatus.facebook == 'error-other'">
				<div class="fb-invite">
					<!-- Text explaining the purpose of the connecting makes no sense if we have a list of friends above already -->
					<p v-if="!friendsExists" v-html="t.mainLeaderboardIntroText" />

					<button class="btn-login-facebook" :class="{'mt-4': friendsExists}" @click.stop="facebookLogin()">
						<FontAwesomeIcon :icon="['fab', 'facebook-f']" />
						{{ t.mainLeaderboardFacebookConnectButton }}
					</button>
				</div>
			</div>

			<div v-if="connectionStatus.facebook == 'error-duplicate-account'">
				<div class="default-block-text alert alert-danger">
					{{ t.generalErrorFbDuplicateAccount }}
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, toRaw } from "vue";
import { useStore } from '@/js/stores/index';
import { useUserStore } from "@/js/stores/user";

import UserThumbnail from "./user-thumbnail.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import Loader from "../loader.vue";
import { authenticateOauth, processLoginError } from "@/js/includes/auth";
import { $emit } from "@/js/includes/event-bus";
import { updateUserPoints } from "@/js/classes/user";
import { useChallenges } from "@/js/stores/challenges";

const store = useStore()
const userStore = useUserStore()
const { removeChallengeByType } = useChallenges()

// Props
defineProps({
	showTitle: {
		type: Boolean,
		default: true,
	}
})

// Computeds
const loading = computed(() => connectionStatus.value.facebook == 'connecting')
const t = computed(() => store.translations)
const connectionStatus = computed(() => userStore.connectionStatus)
const friends = computed(() => {
	// Sort list by points
	let friends = toRaw(userStore.friends) || []

	// Set current value of points for current user so we can sort the leaderboard dynamically
	friends.map((item: {
		me: boolean,
		points: number
	}) => {
		if(item.me){
			item.points = userStore.userPoints
		}
	})

	// Sort descending by points
	friends.sort((a: { points: number }, b: { points: number }) => b.points - a.points)
	
	return friends
})
const friendsExists = computed(() => Object.keys(userStore.friends || {}).length > 1) // There is always me in the list of friends, so need to count over 1
const points = computed(() => userStore.userPoints > 0 ? userStore.userPoints : 0)
const userFullName = computed(() => userStore.userFullName )
const userImage = computed(() => userStore.userImage )

// Methods
function facebookLogin(){
	authenticateOauth("facebook")
		.then(() => {
			$emit('facebook-connected')
			updateUserPoints() // User can complete the challenge so let's try to update points
			removeChallengeByType('facebook_login')
		})
		.catch(e => processLoginError(e, "facebook"))
}
</script>